// ENGINE-LOCAL-FIRST — local milestone mirror (offline trail advancement).
//
// Companion to localAttemptStore (PR #258). The Math→Reading→Mixed trail
// loops offline for the SAME reason attempts didn't adapt: the only
// write/read path for `session_completed` markers is Supabase. finalizeSession
// (in each session screen) writes the trail marker via logSessionCompleted →
// milestone_events, but that write is gated on a real auth session
// (`!persistEnabled`) + a resolved canonical id; offline neither holds, so NO
// marker persists. PathHome's trail reads milestones by the canonical cloud id
// → empty offline → completedSubjectsToday never sees a completion →
// nextTrailSession returns the same row forever (the loop).
//
// This module mirrors `session_completed` markers to localStorage, keyed by
// the LOCAL child id (app.activeChildId — the same id localAttemptStore +
// childrenStore use). PathHome merges them into its milestone read so the
// trail advances offline. The pure trailState helpers (completedSubjectsToday
// / dayCompletedToday / nextTrailSession) are UNCHANGED — they just receive
// the real (local) markers.
//
// SCOPE: the trail-completion marker (session_completed), the read-log
// marker (story_read, as of MA-176), the story-unlock confirmation
// (story_unlocked), the open-log marker (book_opened), the favorite
// toggle (favorite_set/favorite_unset, as of MA-256/MA-177), the
// quit/abandonment marker (session_exit, as of MA-331), and the kid
// self-report marker (session_feedback, as of MA-624) are all
// mirrored — session_exit and session_feedback are mirrored
// unconditionally on every confirmed exit / tap, same posture as
// session_completed, so an abandoned session (or a pre-CHECK-widening tap)
// isn't lost if the device never reconnects before app close. story_unlocked's
// write is still gated on a REAL persisted
// result first when a real session exists (DayCompleteCelebration's
// await-then-confirm contract; a failed cloud write must surface
// retry/terminal, never a fake "Unlocked") — the local mirror is written
// ONLY AFTER that cloud write succeeds, on a permanent cloud-write failure
// (MA-374, so the earned reward isn't lost if the device never comes back
// online before its retry budget is spent), or, without a real session, as
// the local-only write itself. story_read/book_opened/favorite_* carry no
// such contract (no reward, no confirm UI) so they are mirrored
// unconditionally on every finish, matching session_completed's posture —
// this is what keeps them cross-device-coherent even offline. MA-374:
// outboxDrain.ts's _drainMilestones uploads every type mirrored here to the
// cloud on the next verified-online transition (idempotent via the
// deterministic event_key upsert) — book_impression is the one milestone
// type that stays cloud-only (never mirrored, see DayCompleteCelebration).
//
// MA-892: badge_earned (one row per badge record, written by
// src/lib/engine/badges/persistence.ts latchBadges) is mirrored too. A badge
// is a record that is never taken away (KID-JOURNEY D-KJ-9), so its rows have
// their own dedup branch (one row per badge id), appendLocalMilestone never
// stores a second copy of the same badge, and trimToCap never evicts one (the
// CAP bounds the OTHER types; badge rows ride on top, bounded by the
// catalogue).
//
// MA-176 note on mergeMilestones' dedupKey below: no new branch was needed
// for story_read. Its contextData always carries a non-empty `runId` (a
// fresh id per StoryReader mount — see milestoneLogger.buildStoryReadEvent),
// so it already falls into the generic `runId != null` branch, producing
// `story_read|<runId>|` (contextData has no `subject` field, so `cd.subject
// ?? ""` correctly defaults to the empty string) — a key that is unique per
// completed read and identical between a cloud row and its local mirror of
// the SAME read, exactly like session_completed's runId+subject branch.

import type { MilestoneEvent } from "./milestoneLogger";

const PREFIX = "pencilwise-milestones-";
const CAP = 500;

const keyFor = (childId: string) => `${PREFIX}${childId}`;

// MA-980 (D-1013-20): the read reports `unreadable` (getItem threw, the JSON
// did not parse, or the value is not an array) separately from empty. readRaw
// keeps its [] result for every existing caller; only the strict exports below
// surface the flag.
function readRawResult(childId: string | null | undefined): { rows: MilestoneEvent[]; unreadable: boolean } {
  if (typeof window === "undefined" || !childId) return { rows: [], unreadable: false };
  try {
    const raw = window.localStorage.getItem(keyFor(childId));
    if (!raw) return { rows: [], unreadable: false };
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? { rows: parsed as MilestoneEvent[], unreadable: false }
      : { rows: [], unreadable: true };
  } catch {
    return { rows: [], unreadable: true };
  }
}

function readRaw(childId: string | null | undefined): MilestoneEvent[] {
  return readRawResult(childId).rows;
}

function eventAtMs(e: MilestoneEvent): number {
  const t = e.eventAt ? Date.parse(e.eventAt) : NaN;
  return Number.isFinite(t) ? t : 0;
}

/** A row's sync identity — SAME formula mergeMilestones' dedup uses, so a
 *  cloud copy and its local mirror of the same marker always agree on
 *  identity, and the drain confirm-set below (MA-376) can share it too. */
export function milestoneDedupKey(e: MilestoneEvent): string {
  const cd = (e.contextData ?? {}) as Record<string, unknown>;
  const runId = cd.runId;
  if (runId != null && runId !== "") {
    return `${e.eventType}|${runId}|${cd.subject ?? ""}`;
  }
  if (e.eventType === "story_unlocked") {
    return `story_unlocked|${cd.slug ?? "unknown"}`;
  }
  if (e.eventType === "badge_earned") {
    // MA-892: one identity per badge record (the cloud key's badgeId), so a
    // cloud row and its local mirror agree and a re-write dedupes locally.
    return `badge_earned|${cd.badgeId ?? "unknown"}`;
  }
  return `${e.eventType}|${e.id ?? ""}|${e.eventAt ?? ""}`;
}

// ── MA-376: drain confirm-set + drain-aware cap eviction ────────────────
// Mirrors localAttemptStore's confirm-set (see its comment for the full
// rationale) — milestones had NO local record of which rows actually
// synced, so a long fully-offline stretch could evict an un-drained
// marker at the CAP boundary with zero signal. Tracking confirmed-ness by
// identity key lets eviction prefer already-safe rows and count the rest.
const CONFIRMED_PREFIX = "pencilwise-milestones-confirmed-";
const EVICTED_UNDRAINED_PREFIX = "pencilwise-milestones-evicted-undrained-";

function loadConfirmedKeysResult(childId: string): { keys: Set<string>; unreadable: boolean } {
  if (typeof window === "undefined") return { keys: new Set(), unreadable: false };
  try {
    const raw = window.localStorage.getItem(`${CONFIRMED_PREFIX}${childId}`);
    if (!raw) return { keys: new Set(), unreadable: false };
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? { keys: new Set(parsed), unreadable: false }
      : { keys: new Set(), unreadable: true };
  } catch {
    return { keys: new Set(), unreadable: true };
  }
}

function loadConfirmedKeys(childId: string): Set<string> {
  return loadConfirmedKeysResult(childId).keys;
}

function saveConfirmedKeys(childId: string, keys: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    // MA-892 (RED round 1 MUST-1): badge rows ride above the mirror's CAP
    // (trimToCap exempts them), so the CAP cut applies to the other keys
    // only and every badge key is kept. Cutting all keys to CAP left about
    // one synced row per badge unconfirmed for good: the drain re-uploaded
    // it on every tick and the "Saving…" line never cleared.
    const all = Array.from(keys);
    const badge = all.filter((k) => k.startsWith("badge_earned|"));
    const arr = [...all.filter((k) => !k.startsWith("badge_earned|")).slice(-CAP), ...badge];
    window.localStorage.setItem(`${CONFIRMED_PREFIX}${childId}`, JSON.stringify(arr));
  } catch {
    /* best-effort */
  }
}

/** Mark milestones as confirmed-synced. Called by outboxDrain after a
 *  successful upload. */
export function markMilestonesConfirmed(
  childId: string | null | undefined,
  events: readonly MilestoneEvent[],
): void {
  if (typeof window === "undefined" || !childId || events.length === 0) return;
  const keys = loadConfirmedKeys(childId);
  for (const e of events) keys.add(milestoneDedupKey(e));
  saveConfirmedKeys(childId, keys);
}

/** The confirmed-synced key set for a child, read (and parsed) ONCE — for
 *  callers that test many milestones (outboxPending), so they don't re-parse
 *  localStorage per row the way repeated isMilestoneConfirmed calls would. */
export function loadConfirmedMilestoneKeys(childId: string | null | undefined): Set<string> {
  // MA-980 (RED r1 S1): one body; the keys are exactly the strict twin's.
  return tryLoadConfirmedMilestoneKeys(childId).keys;
}

/** MA-980 (D-1013-20): loadConfirmedMilestoneKeys' strict twin (same keys,
 *  plus `unreadable`). */
export function tryLoadConfirmedMilestoneKeys(
  childId: string | null | undefined,
): { keys: Set<string>; unreadable: boolean } {
  return childId ? loadConfirmedKeysResult(childId) : { keys: new Set<string>(), unreadable: false };
}

/** Is this milestone already confirmed-synced? Exposed so outboxDrain can
 *  skip re-uploading rows it already confirmed on a prior drain, bounding
 *  a healthy periodic tick's cost the same way loadUnconfirmedAttempts
 *  does for attempts. */
export function isMilestoneConfirmed(
  childId: string | null | undefined,
  event: MilestoneEvent,
): boolean {
  if (!childId) return false;
  return loadConfirmedKeys(childId).has(milestoneDedupKey(event));
}

function loadEvictedUndrainedCount(childId: string): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(`${EVICTED_UNDRAINED_PREFIX}${childId}`);
    const n = raw ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function recordEvictedUndrained(childId: string, additional: number): void {
  if (typeof window === "undefined" || additional <= 0) return;
  try {
    const total = loadEvictedUndrainedCount(childId) + additional;
    window.localStorage.setItem(`${EVICTED_UNDRAINED_PREFIX}${childId}`, String(total));
  } catch {
    /* best-effort */
  }
}

/** Read-and-clear the undrained-eviction counter — see
 *  localAttemptStore.consumeUndrainedEvictionCount for the one-shot
 *  rationale; useOutboxDrain sums both counters into one signal. */
export function consumeUndrainedEvictionCount(childId: string | null | undefined): number {
  if (typeof window === "undefined" || !childId) return 0;
  const n = loadEvictedUndrainedCount(childId);
  if (n > 0) {
    try { window.localStorage.removeItem(`${EVICTED_UNDRAINED_PREFIX}${childId}`); } catch { /* best-effort */ }
  }
  return n;
}

/** Trim `arr` to CAP, evicting already-CONFIRMED rows first (oldest-first)
 *  and only falling through to evicting UNCONFIRMED rows — counted via
 *  recordEvictedUndrained — when there aren't enough confirmed rows to
 *  absorb the overflow. Mirrors localAttemptStore.trimToCap. */
/** MA-892: rows trimToCap never evicts and the CAP does not count. */
function isEvictionExempt(e: MilestoneEvent): boolean {
  return e.eventType === "badge_earned";
}

function trimToCap(childId: string, arr: MilestoneEvent[]): MilestoneEvent[] {
  // MA-892: badge rows are exempt (a badge is never taken away); the CAP
  // applies to the other rows, and the badge rows keep their positions.
  if (arr.length <= CAP) return arr;
  const evictable = arr.filter((e) => !isEvictionExempt(e));
  if (evictable.length <= CAP) return arr;
  const confirmed = loadConfirmedKeys(childId);
  let toEvict = evictable.length - CAP;
  const evicted = new Set<MilestoneEvent>();
  for (const e of evictable) {
    if (toEvict > 0 && confirmed.has(milestoneDedupKey(e))) {
      evicted.add(e);
      toEvict -= 1;
    }
  }
  let evictedUndrained = 0;
  for (const e of evictable) {
    if (toEvict <= 0) break;
    if (evicted.has(e)) continue;
    evicted.add(e);
    toEvict -= 1;
    evictedUndrained += 1;
  }
  if (evictedUndrained > 0) recordEvictedUndrained(childId, evictedUndrained);
  return arr.filter((e) => !evicted.has(e));
}

/** Append one milestone to the local mirror. Stamps eventAt = now when the
 *  caller didn't (buildSessionCompletedEvent leaves it server-defaulted) so
 *  completedSubjectsToday's `eventAt >= midnight` filter sees a real today
 *  stamp. Best-effort: swallows quota / serialization errors. No-op under
 *  SSR / null childId. */
export function appendLocalMilestone(
  childId: string | null | undefined,
  event: MilestoneEvent,
): void {
  if (typeof window === "undefined" || !childId) return;
  try {
    const stamped: MilestoneEvent = event.eventAt
      ? event
      : { ...event, eventAt: new Date().toISOString() };
    const arr = readRaw(childId);
    // MA-892: a badge record is stored once; a re-write of the same badge
    // (a second mount, a second tab) is a no-op.
    if (stamped.eventType === "badge_earned") {
      const k = milestoneDedupKey(stamped);
      if (arr.some((e) => milestoneDedupKey(e) === k)) return;
    }
    arr.push(stamped);
    const capped = trimToCap(childId, arr);
    window.localStorage.setItem(keyFor(childId), JSON.stringify(capped));
  } catch {
    /* best-effort */
  }
}

/** Delete the entire local milestone mirror for `childId` (used by
 *  removeChild so a deleted kid leaves no orphaned milestone history on the
 *  device — B-2/G-6). No-op under SSR / when childId is null. */
export function removeLocalMilestones(childId: string | null | undefined): void {
  if (typeof window === "undefined" || !childId) return;
  try { window.localStorage.removeItem(keyFor(childId)); } catch { /* best-effort */ }
}

/** Load the child's local milestones, optionally filtered to events at/after
 *  `since` (mirrors fetchMilestoneEventsForChild's `since` arg), most-recent
 *  first (matching the cloud reader's descending order), capped to `limit`.
 *
 *  ⭐The default is CAP — the SAME number the write side caps the mirror at
 *  (see `appendLocalMilestone`) — so "read the mirror" means "read the whole
 *  mirror" unless a caller deliberately asks for less. It used to default to
 *  100 while CAP was 500, making the default read a silent TRUNCATION that
 *  keeps the NEWEST rows and drops the OLDEST. Three call sites omitted the
 *  argument (PathHome, SessionRecap, dataExport) and three passed 500
 *  (mergeEarnedShelfEvents, outboxDrain, DayCompleteCelebration), so the
 *  reward screens read the same ledger through different windows. Once a
 *  child accumulated >100 mirrored rows inside the round-carryover window,
 *  the oldest row dropped was a prior day's `story_unlocked` pick — exactly
 *  the row `unclaimedRoundsCarryover` and `pendingRoundSlot` count. PathHome
 *  then offered a reward (unlocks 1, slot 2) that DayCompleteCelebration did
 *  not believe existed (unlocks 0, slot 3) and rendered its zero-card
 *  branch; because the SLOTS also disagreed, the celebration-shown latch
 *  never matched and /play auto-navigated back into the dead end on every
 *  mount. Regression: `rewardUnlockReadParity.test.mjs`.
 *
 *  The DIVERGENT default was the defect, not the number 100 itself: a caller
 *  that genuinely wants a small page should still pass an explicit limit. */
export function loadLocalMilestones(
  childId: string | null | undefined,
  since?: Date,
  limit = CAP,
): MilestoneEvent[] {
  // MA-980 (RED r1 S1): one body; the rows are exactly the strict twin's.
  return tryLoadLocalMilestones(childId, since, limit).rows;
}

/** MA-980 (D-1013-20): loadLocalMilestones' strict twin for the parent kid
 *  card. Same rows; `unreadable` is true when this child's milestone store
 *  could not be read. loadLocalMilestones returns its `.rows`. */
export function tryLoadLocalMilestones(
  childId: string | null | undefined,
  since?: Date,
  limit = CAP,
): { rows: MilestoneEvent[]; unreadable: boolean } {
  if (!childId) return { rows: [], unreadable: false };
  const sinceMs = since ? since.getTime() : -Infinity;
  const raw = readRawResult(childId);
  const rows = raw.rows.filter((e) => eventAtMs(e) >= sinceMs);
  rows.sort((a, b) => eventAtMs(b) - eventAtMs(a)); // descending (newest first)
  return { rows: rows.slice(0, limit), unreadable: raw.unreadable };
}

/** Merge cloud + local milestone lists, de-duped, most-recent first. A cloud
 *  row and its local mirror are the SAME marker (same run + subject); we key
 *  on eventType|runId|subject (with id/eventAt fallback) so a synced row isn't
 *  double-counted. Cloud wins on collision (durable copy once sync exists). */
export function mergeMilestones(
  cloud: readonly MilestoneEvent[],
  local: readonly MilestoneEvent[],
): MilestoneEvent[] {
  const seen = new Set<string>();
  // A session_completed marker's STABLE identity is its client runId +
  // subject — identical on the cloud row and its local mirror (both built
  // from buildSessionCompletedEvent with the same args), even though their
  // server id / eventAt differ. MA-372: story_unlocked carries no runId
  // (built inline in DayCompleteCelebration), so it used to fall to the
  // id|eventAt fallback — a cloud row (server id, server eventAt) and its
  // own local mirror (no id, locally-stamped eventAt) never matched, so
  // BOTH survived the merge and unlocksAvailableToday double-counted every
  // online claim; its stable identity is one-per-(child,slug), the SAME
  // identity milestoneEventKey uses server-side for its event_key
  // (`childId:story_unlocked:<slug>`), so it keys on slug too. Event types
  // with no runId and no dedicated branch fall back to id|eventAt — safe
  // ONLY because those types are never locally mirrored (cloud rows alone
  // are already de-duped server-side by their own event_key upsert), so no
  // local-mirror pair ever reaches that fallback. Adding a new locally-
  // mirrored event type with no runId requires its own branch, mirroring
  // this one. See milestoneDedupKey above (this is the exact same formula,
  // shared with the MA-376 drain confirm-set).
  const dedupKey = milestoneDedupKey;
  const out: MilestoneEvent[] = [];
  for (const e of cloud) {
    const k = dedupKey(e);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(e);
    }
  }
  for (const e of local) {
    const k = dedupKey(e);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(e);
    }
  }
  return out.sort((a, b) => eventAtMs(b) - eventAtMs(a));
}

/** MA-346 — the shared shelf-surface composition: a cloud `story_unlocked`
 *  fetch (already type-narrowed by the caller) merged with the local mirror,
 *  so an offline/pre-link earn is never told-then-untold (the trail says
 *  "You unlocked {title}!" but /read + /library used to read cloud-only and
 *  show nothing). One function, not re-derived per call site, so the
 *  cloud/local argument order to `mergeMilestones` can't be transposed at
 *  the ReadTab/library/StoryReader call sites — `earnedSlugsFromEvents` /
 *  `earnedStoriesFromEvents` / `specialEditionSlugsFromEvents` each filter to
 *  `story_unlocked` internally, so passing the mirror's other locally-
 *  mirrored types (session_completed, story_read, book_opened,
 *  favorite_set/unset) through unfiltered is safe. */
export function mergeEarnedShelfEvents(
  cloud: readonly MilestoneEvent[],
  localChildId: string | null | undefined,
): MilestoneEvent[] {
  return mergeMilestones(cloud, loadLocalMilestones(localChildId, undefined, 500));
}
